import React, { useState, useEffect } from "react";
import { ChevronDown, XIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../@/components/ui/dropdown-menu";
import { useGetTagsValuesMutation } from "../api/api";

interface TagSelectorProps {
  fetchTagId: any;
  selectedTags: Tag[];
  setSelectedTags: React.Dispatch<React.SetStateAction<Tag[]>>;
  preloadedTags?: PreloadedTag[];
  removedTags?: RemovedTags[];
  setRemovedTags?: React.Dispatch<React.SetStateAction<RemovedTags[]>>;
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
  maxTags?: number;
  serviceTypeId?: number;
  paramGetServiceTypeId?: number;
}

interface Tag {
  id: number;
  name: string;
  serviceTypeId?: number;
}

interface PreloadedTag {
  id: number;
  productId: number;
  tagId: number;
  tagName: string;
}

interface RemovedTags {
  id: number;
}

const TagSelector: React.FC<TagSelectorProps> = ({
  fetchTagId,
  setSelectedTags,
  selectedTags,
  preloadedTags,
  removedTags = [],
  setRemovedTags,
  maxTags,
  setShowAlertOnRouteChange,
  serviceTypeId,
  paramGetServiceTypeId,
}) => {
  const [open, setOpen] = useState(false);
  const [getTags] = useGetTagsValuesMutation();
  const [tagsValues, setTagsValues] = useState<Tag[]>([]);

  useEffect(() => {
    const fetchTags = async () => {
      setShowAlertOnRouteChange(false);
      try {
        const response = await getTags({
          body: { tagId: fetchTagId },
        }).unwrap();

        const filteredTags = response?.data.filter((tag: Tag) => {
          const matchesServiceTypeId = serviceTypeId
            ? tag.serviceTypeId === serviceTypeId
            : true;
          const matchesParamServiceTypeId = paramGetServiceTypeId
            ? tag.serviceTypeId === Number(paramGetServiceTypeId)
            : true;
          return matchesServiceTypeId && matchesParamServiceTypeId;
        });

        setTagsValues(filteredTags);
        setShowAlertOnRouteChange(false);
      } catch (error) {
        console.error("Failed to fetch tags:", error);
      }
    };
    fetchTags();
  }, [fetchTagId, getTags, serviceTypeId, paramGetServiceTypeId]);

  useEffect(() => {
    if (preloadedTags) {
      const initialTags = tagsValues.filter((tag) =>
        preloadedTags.some((pt) => pt.tagId === tag.id)
      );
      setSelectedTags(maxTags ? initialTags.slice(0, maxTags) : initialTags);
      setShowAlertOnRouteChange(false);
    }
  }, [preloadedTags, tagsValues, setSelectedTags, maxTags]);

  const toggleTagSelection = (tag: Tag) => {
    const isSelected = selectedTags.some((t) => t.id === tag.id);
    const canAddMore = !maxTags || selectedTags.length < maxTags;

    if (isSelected || canAddMore) {
      setShowAlertOnRouteChange(true);
      setSelectedTags((prevTags) => {
        const updatedTags = isSelected
          ? prevTags.filter((t) => t.id !== tag.id)
          : [...prevTags, tag];
        return maxTags ? updatedTags.slice(0, maxTags) : updatedTags;
      });

      if (setRemovedTags) {
        const preloadedTag = preloadedTags?.find((pt) => pt.tagId === tag.id);

        setRemovedTags((prevRemoved) => {
          if (isSelected && preloadedTag) {
            return !prevRemoved.some((rt) => rt.id === preloadedTag.id)
              ? [...prevRemoved, { id: preloadedTag.id }]
              : prevRemoved;
          } else {
            return preloadedTag
              ? prevRemoved.filter((t) => t.id !== preloadedTag.id)
              : prevRemoved;
          }
        });
      }
    }
  };

  const removeTag = (tag: Tag, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAlertOnRouteChange(true);
    setSelectedTags((prevTags) => prevTags.filter((t) => t.id !== tag.id));

    if (setRemovedTags) {
      const preloadedTag = preloadedTags?.find((pt) => pt.tagId === tag.id);

      if (preloadedTag) {
        setRemovedTags((prevRemoved) =>
          !prevRemoved.some((rt) => rt.id === preloadedTag.id)
            ? [...prevRemoved, { id: preloadedTag.id }]
            : prevRemoved
        );
      }
    }
  };

  const isTagLimitReached = maxTags ? selectedTags.length >= maxTags : false;

  return (
    <div className="w-full mt-1 mx-auto">
      <div className="relative">
        {selectedTags.length > 0 && (
          <div className="w-full border border-gray-300 rounded p-2 cursor-pointer flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <div
                key={tag.id}
                className="flex items-center bg-customGreenLight text-customGreenColor border border-customGreenColor px-2 py-1 rounded"
              >
                <span>{tag.name}</span>
                <XIcon
                  className="ml-1 w-4 h-4 cursor-pointer"
                  onClick={(e) => removeTag(tag, e)}
                />
              </div>
            ))}
          </div>
        )}
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <div className="relative text-sm  text-customGrayBase">
              <button className="focus:outline-customGreenColor mt-2 w-full p-2 text-left border border-gray-300 rounded-md bg-grayBackgroundColor">
                Select Tags {maxTags && `(${selectedTags.length}/${maxTags})`}
              </button>
              <div className="absolute right-0 top-5 mr-3">
                <ChevronDown className="w-4 h-4 text-customGrayBase cursor-pointer" />
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[35vw] max-h-96 overflow-y-auto bg-white shadow-lg rounded">
            {tagsValues.map((tag) => (
              <DropdownMenuItem
                key={tag.id}
                onClick={() => toggleTagSelection(tag)}
                className={`p-2 cursor-pointer ${
                  selectedTags.some((t) => t.id === tag.id)
                    ? "bg-customGreenDisabled"
                    : isTagLimitReached
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                disabled={
                  isTagLimitReached &&
                  !selectedTags.some((t) => t.id === tag.id)
                }
              >
                {tag.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TagSelector;
